import { type TaskCreateDto, type TaskPriority, type TaskStatus } from "@crowdcoursing/api/openapi";
import { Flag } from "@mui/icons-material";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { Field, Form } from "react-final-form";

const TASK_PRIORITIES: { label: string; value: TaskPriority }[] = [
  { label: "Urgent", value: 1 },
  { label: "High", value: 2 },
  { label: "Medium", value: 3 },
  { label: "Low", value: 4 },
];

const TASK_STATUSES: { label: string; value: TaskStatus }[] = [
  { label: "To do", value: 1 },
  { label: "In Progress", value: 2 },
  { label: "Blocked", value: 3 },
  { label: "Done", value: 4 },
];

interface TaskFormValues extends TaskCreateDto {
  dueAt: string | null;
  reminderAt: string | null;
}

interface TaskFormProps {
  initialValues?: Partial<TaskFormValues>;
  onSubmit: (values: TaskFormValues) => void;
}

export const TaskForm = ({ initialValues, onSubmit }: TaskFormProps) => {
  return (
    <Form<TaskFormValues>
      initialValues={{
        // Todo
        priority: 4,
        status: 1, // Low
        ...initialValues,
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form id="task-form" onSubmit={handleSubmit}>
          <Box display="grid" gap={3}>
            {/* Title */}
            <Field name="title">
              {({ input }) => (
                <TextField {...input} label="Title" required fullWidth placeholder="What needs to be done?" />
              )}
            </Field>

            {/* Description */}
            <Field name="description">
              {({ input }) => (
                <TextField
                  {...input}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional details..."
                />
              )}
            </Field>

            {/* Priority and Status */}
            <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr">
              <Field name="priority">
                {({ input }) => (
                  <FormControl fullWidth>
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select {...input} labelId="priority-label" label="Priority">
                      {TASK_PRIORITIES.map((priority) => (
                        <MenuItem key={priority.value} value={priority.value}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Flag
                              sx={{
                                color:
                                  priority.value === 1
                                    ? "#d32f2f" // Urgent - Deeper red
                                    : priority.value === 2
                                      ? "#f57c00" // High - Orange
                                      : priority.value === 3
                                        ? "warning.main" // Medium - Yellow
                                        : "primary.main", // Low
                              }}
                              fontSize="small"
                            />
                            {priority.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>

              <Field name="status">
                {({ input }) => (
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select {...input} labelId="status-label" label="Status">
                      {TASK_STATUSES.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>
            </Box>

            {/* Due Date and Reminder */}
            <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr">
              <Field name="dueAt">
                {({ input }) => (
                  <TextField
                    label="Due Date"
                    type="datetime-local"
                    value={input.value ? DateTime.fromISO(input.value).toFormat("yyyy-MM-dd'T'HH:mm") : ""}
                    onChange={(e) => {
                      const date = e.target.value ? DateTime.fromISO(e.target.value).toISO() : null;
                      input.onChange(date);
                    }}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              </Field>

              <Field name="reminderAt">
                {({ input }) => (
                  <TextField
                    label="Reminder"
                    type="datetime-local"
                    value={input.value ? DateTime.fromISO(input.value).toFormat("yyyy-MM-dd'T'HH:mm") : ""}
                    onChange={(e) => {
                      const date = e.target.value ? DateTime.fromISO(e.target.value).toISO() : null;
                      input.onChange(date);
                    }}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              </Field>
            </Box>
          </Box>
        </form>
      )}
    />
  );
};
